import { useState, useMemo, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { DashboardBanner } from '../dasboardBanner/dashboardBanner';
import DashboardFilters from './DashboardFilters';
import UnitsTable from './UnitsTable';
import { calculateDashboardStatistics } from './dashboardStatistics';
import { DEFAULT_FILTERS } from '../../constants/appConstants';
import { filterDashboardUnits, compareByCriteria } from '../dashboardUtils';
import { getProperty } from '../../utils/formatters';
import { getUnitProperty, calculateHasErrors } from '../../schemas/unitSchema';
import { validateIssue } from '../../schemas/issueSchema';

function MainTable({
  dashboardUnitsComplete,
  groupedDataByUnit,
  classifiedIssuesbyUnitAndKey,
  handleUnit,
  handleCloseAllIssues
}) {
  const [isClosingAll, setIsClosingAll] = useState(false);

  // Flat list of every issue across all units, for the global "delete all"
  // action — classifiedIssuesbyUnitAndKey groups issues two levels deep
  // (unit -> combinedKey -> issues), so this flattens them.
  const allOpenIssues = useMemo(() => {
    return Object.values(classifiedIssuesbyUnitAndKey || {})
      .flatMap(unitGroups => Object.values(unitGroups).flat())
      .filter(issue => validateIssue(issue));
  }, [classifiedIssuesbyUnitAndKey]);
  // Filter state
  const [organizationFilter, setOrganizationFilter] = useState(DEFAULT_FILTERS.organization);
  const [centerFilter, setCenterFilter] = useState(DEFAULT_FILTERS.center);
  const [selectedStatusesFilter, setSelectedStatusesFilter] = useState(DEFAULT_FILTERS.selectedStatuses);
  const [generalSearchFilter, setGeneralSearchFilter] = useState(DEFAULT_FILTERS.generalSearch);
  
  // Sort state
  const [sortField, setSortField] = useState('unit');
  const [sortOrder, setSortOrder] = useState('asc');

  // Handle sorting
  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  }, [sortField]);

  // Process dashboard units (filter and sort)
  const dashboardUnitsProcessed = useMemo(() => {
    if (!dashboardUnitsComplete || Object.keys(dashboardUnitsComplete).length === 0) {
      return [];
    }

    const filters = {
      unitIdFilter: '',
      organizationFilter,
      centerFilter,
      selectedStatusesFilter
    };

    const filtered = filterDashboardUnits(dashboardUnitsComplete, filters, []);

    let filteredUnits = Object.values(filtered);

    if (generalSearchFilter) {
      const searchTerm = generalSearchFilter.toLowerCase().trim();
      filteredUnits = filteredUnits.filter(unit => {
        const unitId = getUnitProperty(unit, 'unit', '').toString().toLowerCase();
        const organization = getUnitProperty(unit, 'organization', '').toString().toLowerCase();
        const center = getUnitProperty(unit, 'center', '').toString().toLowerCase();
        return unitId.includes(searchTerm) || organization.includes(searchTerm) || center.includes(searchTerm);
      });
    }

    // Add hasErrors field to each unit
    const validUnits = filteredUnits
      .map(unit => ({
        ...unit,
        hasErrors: calculateHasErrors(unit, classifiedIssuesbyUnitAndKey)
      }));
    
    // Apply sorting
    return validUnits.sort((a, b) => {
      if (sortField === 'unit_status' || sortField === 'status' || sortField === 'time') {
        return compareByCriteria(a, b, groupedDataByUnit);
      }

      const compareA = getProperty(a, sortField)?.toString();
      const compareB = getProperty(b, sortField)?.toString();

      if (compareA === null || compareA === undefined) {
        return sortOrder === 'asc' ? 1 : -1;
      }

      if (compareB === null || compareB === undefined) {
        return sortOrder === 'asc' ? -1 : 1;
      }

      if (sortOrder === 'asc') {
        return compareA.localeCompare(compareB, undefined, { numeric: true });
      } else {
        return compareB.localeCompare(compareA, undefined, { numeric: true });
      }
    });
  }, [
    dashboardUnitsComplete, 
    generalSearchFilter,
    organizationFilter, 
    centerFilter, 
    selectedStatusesFilter, 
    sortField, 
    sortOrder, 
    groupedDataByUnit,
    classifiedIssuesbyUnitAndKey 
  ]);

  // Memoize organization list for dropdown
  const organizationList = useMemo(() => {
    if (!dashboardUnitsComplete || Object.keys(dashboardUnitsComplete).length === 0) {
      return [];
    }
    return Array.from(new Set(
      Object.values(dashboardUnitsComplete)
        .map(item => getUnitProperty(item, 'organization', ''))
        .filter(org => org && org !== '-')
    )).sort();
  }, [dashboardUnitsComplete]);

  // Memoize center list for dropdown
  const centerList = useMemo(() => {
    if (!dashboardUnitsComplete || Object.keys(dashboardUnitsComplete).length === 0) {
      return [];
    }
    
    // If organization is selected, filter centers by that organization
    const units = organizationFilter 
      ? Object.values(dashboardUnitsComplete).filter(item => {
          const organization = getUnitProperty(item, 'organization', '');
          return organization?.toLowerCase() === organizationFilter.toLowerCase();
        })
      : Object.values(dashboardUnitsComplete);
    
    return Array.from(new Set(
      units.map(item => getUnitProperty(item, 'center', '')).filter(center => center && center !== '-')
    )).sort();
  }, [dashboardUnitsComplete, organizationFilter]);

  // Calculate dashboard statistics
  const dashboardStatistics = useMemo(() => 
    calculateDashboardStatistics(dashboardUnitsProcessed),
    [dashboardUnitsProcessed]
  );

  const handleDeleteAllOpenIssues = async () => {
    if (allOpenIssues.length === 0) return;
    if (window.confirm(`Are you sure you want to close all ${allOpenIssues.length} open issues across every unit? This cannot be undone.`)) {
      setIsClosingAll(true);
      try {
        await handleCloseAllIssues(allOpenIssues);
      } finally {
        setIsClosingAll(false);
      }
    }
  };

  return (
    <div className='py-4'>
      <div className="text-start mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h5><strong>MONITORING</strong></h5>
        {allOpenIssues.length > 0 && (
          <Button
            variant="danger"
            size="sm"
            disabled={isClosingAll}
            onClick={handleDeleteAllOpenIssues}
          >
            {isClosingAll ? 'Deleting…' : `Delete All Open Issues (${allOpenIssues.length})`}
          </Button>
        )}
      </div>
      <DashboardBanner {...dashboardStatistics} />
      <DashboardFilters
        organizationList={organizationList}
        centerList={centerList}
        organizationFilter={organizationFilter}
        setOrganizationFilter={setOrganizationFilter}
        centerFilter={centerFilter}
        setCenterFilter={setCenterFilter}
        selectedStatusesFilter={selectedStatusesFilter}
        setSelectedStatusesFilter={setSelectedStatusesFilter}
        generalSearchFilter={generalSearchFilter}
        setGeneralSearchFilter={setGeneralSearchFilter}
      />
      <UnitsTable 
        units={dashboardUnitsProcessed}
        handleSort={handleSort}
        handleUnit={handleUnit}
        classifiedIssuesbyUnitAndKey={classifiedIssuesbyUnitAndKey}
      />
    </div>
  );
}

export default MainTable;